import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

// daisyUI theme names (see src/index.css): emerald = light, dark = dark.
type Theme = 'emerald' | 'dark';

// Read the theme the no-flash init script in index.html already applied to
// <html>, so the button starts in sync with what's on screen. Falls back to
// the OS preference if the attribute is somehow missing.
function getInitialTheme(): Theme {
  const current = document.documentElement.getAttribute('data-theme');
  if (current === 'dark' || current === 'emerald') {
    return current;
  }
  const prefersDark =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'emerald';
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('theme', theme);
    } catch {
      /* storage disabled (e.g. private mode) — theme still applies for the session */
    }
  }, [theme]);

  const isDark = theme === 'dark';
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <button
      className="btn btn-ghost btn-square mr-4"
      type="button"
      onClick={() => setTheme((prev) => (prev === 'dark' ? 'emerald' : 'dark'))}
      aria-label={label}
      title={label}
    >
      {isDark ? <SunIcon className="w-7 h-7" /> : <MoonIcon className="w-7 h-7" />}
    </button>
  );
}
